import React, { useEffect, useRef, useState } from "react";
import {
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
} from "components-lib";
import SmartAutocompleteInput from "../../components/SmartAutocompleteInput";
import { AutocompleteProvider } from "../../components/AutocompleteProvider";
import {
  MEDICINE_TYPES,
  DOSAGE_OPTIONS,
  FREQUENCY_PATTERNS,
  EYE_DROP_FREQUENCIES,
  EYE_OINTMENT_FREQUENCIES,
  SKIN_OINTMENT_FREQUENCIES,
  DURATION_OPTIONS,
  HOW_TO_TAKE_OPTIONS,
  getFrequencyDisplay,
  getFrequencyText,
} from "../utils/medicineDataStructure";
import {
  saveToStorage as saveToCentralStorage,
  getStoredSuggestions as getCentralStoredSuggestions,
  STORAGE_KEYS as CENTRAL_STORAGE_KEYS,
} from "views/admin/doctors/components/utils/storageUtils";

/**
 * MedicinesSection Component
 *
 * Manages the medicines table with enhanced autocomplete functionality and allergy checking
 *
 * FEATURES:
 * - Displays existing medicines with allergy conflict highlighting
 * - Provides form for adding new medicines with real-time allergy validation
 * - Shows visual warnings (red highlighting, warning icons) for allergy conflicts
 * - Displays confirmation modal when attempting to add conflicting medicines
 * - Includes date calculations and API-based suggestions
 * - Highlights existing medicines that conflict with patient allergies
 *
 * ALLERGY CHECKING:
 * - Performs case-insensitive partial matching between medicine names and known allergies
 * - Shows red border and background for conflicting medicine inputs
 * - Displays warning icon (!) for both new and existing conflicting medicines
 * - Changes Add button color to red with warning emoji when conflict detected
 * - Shows summary alert if any existing medicines have allergy conflicts
 *
 * PROPS:
 * - allergicMedicines: Array of medicine names the patient is allergic to
 * - All existing props for medicine management functionality
 */
/*
  MedicinesSection: consumes `templateData` prop and merges normalized medicines
  into the current `medicinesData` via `setMedicinesData`.

  Behavior:
  - Listens to `templateData` in a useEffect (see bottom of file) and normalizes
    `templateData.medicines` (supports both array or object shapes).
  - Performs fuzzy dedupe before appending to preserve existing entries.

  See `docs/LOAD_TEMPLATE_FLOW.md` for the canonical flow and pointers.
*/

const MedicinesSection = ({
  medicinesData,
  setMedicinesData,
  newMedicineData,
  setNewMedicineData,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  onAddMedicine,
  onDeleteMedicine,
  onDurationChange,
  apiRecommendations,
  // Allergy data for checking conflicts
  allergicMedicines = [],
  appointmentId,
  templateData,
  appointmentPrescription,
}) => {
  // Modal state for allergy confirmation
  const {
    isOpen: isAllergyModalOpen,
    onOpen: onAllergyModalOpen,
    onClose: onAllergyModalClose,
  } = useDisclosure();
  const [conflictingMedicine, setConflictingMedicine] = useState("");
  const [pendingMedicineData, setPendingMedicineData] = useState(null);
  const hydratedFromPrescriptionRef = useRef(null);

  /**
   * Check if a medicine name conflicts with known allergies
   * Performs case-insensitive partial matching
   */
  const checkAllergyConflict = (medicineName) => {
    if (
      !medicineName ||
      !allergicMedicines ||
      allergicMedicines.length === 0
    ) {
      return null;
    }

    const medicineNameLower = medicineName.toLowerCase().trim();

    // Check for exact matches and partial matches
    const conflictingAllergy = allergicMedicines.find((allergy) => {
      const allergyLower = allergy.toLowerCase().trim();
      return (
        // Exact match
        medicineNameLower === allergyLower ||
        // Medicine name contains allergy
        medicineNameLower.includes(allergyLower) ||
        // Allergy contains medicine name
        allergyLower.includes(medicineNameLower)
      );
    });

    return conflictingAllergy || null;
  };

  /**
   * Get user-friendly conflict message for display
   */
  const getConflictMessage = (medicineName, conflictingAllergy) => {
    if (!medicineName || !conflictingAllergy) return "";

    const medicineNameLower = medicineName.toLowerCase().trim();
    const allergyLower = conflictingAllergy.toLowerCase().trim();

    if (medicineNameLower === allergyLower) {
      return `Patient is directly allergic to ${conflictingAllergy}`;
    } else if (medicineNameLower.includes(allergyLower)) {
      return `Medicine contains ${conflictingAllergy}, which patient is allergic to`;
    } else if (allergyLower.includes(medicineNameLower)) {
      return `Patient's allergy to ${conflictingAllergy} may include this medicine`;
    }

    return `May conflict with patient's allergy to ${conflictingAllergy}`;
  };

  /**
   * Enhanced add medicine handler with allergy checking
   */
  const handleAddMedicineWithAllergyCheck = () => {
    const medicineName = newMedicineData.name?.trim();

    if (!medicineName) {
      // If no medicine name, proceed with original add function
      onAddMedicine();
      return;
    }

    // Check for allergy conflicts
    const conflictingAllergy = checkAllergyConflict(medicineName);

    if (conflictingAllergy) {
      // Store the medicine data and show confirmation modal
      setConflictingMedicine(conflictingAllergy);
      setPendingMedicineData({ ...newMedicineData });
      onAllergyModalOpen();
    } else {
      // No conflict, proceed with adding medicine
      onAddMedicine();
    }
  };

  /**
   * Confirm adding medicine despite allergy conflict
   */
  const confirmAddMedicineWithAllergy = () => {
    // Proceed with adding the medicine
    onAddMedicine();
    // Close modal and reset state
    onAllergyModalClose();
    setConflictingMedicine("");
    setPendingMedicineData(null);
  };

  /**
   * Cancel adding medicine due to allergy conflict
   */
  const cancelAddMedicine = () => {
    // Close modal and reset state
    onAllergyModalClose();
    setConflictingMedicine("");
    setPendingMedicineData(null);
  };

  // Frequency mapping - maps 4-character patterns to readable text for internal state
  // Frontend displays symbols (0 = no dose, | = dose), backend stores readable text
  const FREQUENCY_MAPPING = {
    "0000": "No dose", // 0 - 0 - 0 - 0
    "000|": "Night only", // 0 - 0 - 0 - |
    "00||": "Evening, Night", // 0 - 0 - | - |
    "0|00": "Afternoon only", // 0 - | - 0 - 0
    "0|0|": "Afternoon, Night", // 0 - | - 0 - |
    "0||0": "Afternoon, Evening", // 0 - | - | - 0
    "0|||": "Afternoon, Evening, Night", // 0 - | - | - |
    "||||": "Morning, Afternoon, Evening, Night", // | - | - | - |
    "|000": "Morning only", // | - 0 - 0 - 0
    "|00|": "Morning, Night", // | - 0 - 0 - |
    "|0|0": "Morning, Evening", // | - 0 - | - 0
    "00|0": "Evening only", // 0 - 0 - | - 0
    "|0||": "Morning, Evening, Night", // | - 0 - | - |
    "||00": "Morning, Afternoon", // | - | - 0 - 0
    "||0|": "Morning, Afternoon, Night", // | - | - 0 - |
    "|||0": "Morning, Afternoon, Evening", // | - | - | - 0
  };

  // Get display text for frequency (symbols for frontend)
  const getFrequencyDisplay = (code) => {
    if (!code || code.length !== 4) return code;
    return code.split("").join(" - ");
  };

  // Get readable text for frequency (for internal state)
  const getFrequencyText = (code) => {
    return FREQUENCY_MAPPING[code] || code;
  };

  // LocalStorage management functions
  const getStoredSuggestions = (key) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return [];
    }
  };

  const saveToStorage = (key, value) => {
    try {
      const existing = getStoredSuggestions(key);
      if (!existing.includes(value) && value.trim()) {
        const updated = [...existing, value.trim()];
        localStorage.setItem(key, JSON.stringify(updated));
      }
    } catch (error) {
      console.error(`Error saving ${key} to localStorage:`, error);
    }
  };

  // local alias, prefer central STORAGE_KEYS
  const STORAGE_KEYS = {
    MEDICINES: CENTRAL_STORAGE_KEYS.MEDICINES,
    FREQUENCIES: CENTRAL_STORAGE_KEYS.FREQUENCIES,
  };

  // State to hold ordered frequency options computed from usage data
  const [orderedFrequencyOptions, setOrderedFrequencyOptions] =
    useState(null);

  /**
   * Compute ordered frequencies based on usage stored in localStorage.
   * We try both the central STORAGE_KEYS.FREQUENCIES key and the older 'medicinesFreq' key
   * (some places in the app store frequency usage as 'medicinesFreq').
   *
   * baseOptions should be an array of option objects: { value, label, ... }
   * Returns a new array where options with higher usage counts are placed first.
   */
  const computeOrderedFrequencies = (baseOptions = []) => {
    try {
      const raw =
        localStorage.getItem(STORAGE_KEYS.FREQUENCIES) ||
        localStorage.getItem("medicinesFreq") ||
        "[]";
      const stored = JSON.parse(raw);
      const counts = (Array.isArray(stored) ? stored : []).reduce(
        (acc, f) => {
          if (!f) return acc;
          acc[f] = (acc[f] || 0) + 1;
          return acc;
        },
        {},
      );

      // preserve original index to keep a stable ordering for ties
      const withIndex = baseOptions.map((opt, idx) => ({ opt, idx }));
      withIndex.sort((a, b) => {
        const ca = counts[a.opt.value] || 0;
        const cb = counts[b.opt.value] || 0;
        if (cb === ca) return a.idx - b.idx;
        return cb - ca; // descending by count
      });
      return withIndex.map((w) => w.opt);
    } catch (err) {
      console.error("Failed to compute ordered frequencies:", err);
      return baseOptions;
    }
  };

  /**
   * Called when frequency select receives focus — compute and cache ordered options
   * based on stored usage and current medicine `type`.
   */
  const handleFrequencyFocus = (type) => {
    let base = [];
    if (type === "Eye Drop")
      base = EYE_DROP_FREQUENCIES.filter((f) => f && f.value);
    else if (type === "Eye Ointment")
      base = EYE_OINTMENT_FREQUENCIES.filter((f) => f && f.value);
    else if (type === "Skin Ointment")
      base = SKIN_OINTMENT_FREQUENCIES.filter((f) => f && f.value);
    else
      base = FREQUENCY_PATTERNS.filter(
        (pattern) => (pattern.value || "").toString() !== "0000",
      ).filter((pattern) => pattern && pattern.value);

    const ordered = computeOrderedFrequencies(base);
    setOrderedFrequencyOptions(ordered);
  };

  // Check if there are any existing medicines with allergy conflicts
  const existingConflicts = medicinesData.filter((medicine) =>
    checkAllergyConflict(medicine.name),
  );

  useEffect(() => {
    if (!appointmentId || !appointmentPrescription) return;
    if (hydratedFromPrescriptionRef.current === appointmentId) return;

    try {
      const source = appointmentPrescription.data || appointmentPrescription;
      const apiMedicines = Array.isArray(source?.medicines)
        ? source.medicines
        : Array.isArray(source?.prescription?.medicines)
          ? source.prescription.medicines
          : [];

      if (!apiMedicines.length) return;

      setMedicinesData(
        apiMedicines.map((medicine, idx) => ({
          no: medicine.medicine_no ?? idx + 1,
          name: medicine.name,
          type: medicine.type,
          dosage: medicine.dosage,
          frequency: medicine.frequency,
          duration: medicine.duration,
          howToTake: medicine.when_to_take,
          fromTo: `${medicine.from_date} - ${medicine.to_date}`,
        })),
      );

      hydratedFromPrescriptionRef.current = appointmentId;
    } catch (error) {
      console.error(
        `Error hydrating medicines for appointment ID ${appointmentId}:`,
        error,
      );
    }
  }, [appointmentId, appointmentPrescription, setMedicinesData]);

  useEffect(() => {
    // If no template provided, do nothing (don't overwrite existing medicines)
    if (!templateData) return;

    try {
      let parsed = templateData;
      if (typeof templateData === "string") {
        parsed = JSON.parse(templateData);
      }

      const templateMedicines = Array.isArray(parsed.medicines)
        ? parsed.medicines
        : Array.isArray(parsed)
          ? parsed
          : [];

      const normalized = templateMedicines
        .map((medicine) => ({
          no: medicine.no ?? medicine.medicine_no ?? "",
          name: (medicine.name ?? "").toString().trim(),
          type: (medicine.type ?? "").toString().trim(),
          dosage: (medicine.dosage ?? "").toString().trim(),
          frequency: (medicine.frequency ?? "").toString().trim(),
          duration: (medicine.duration ?? "").toString().trim(),
          howToTake: (
            medicine.howToTake ??
            medicine.when_to_take ??
            ""
          )
            .toString()
            .trim(),
          fromTo:
            medicine.fromTo ??
            (medicine.from_date || medicine.to_date
              ? `${medicine.from_date || ""} - ${medicine.to_date || ""}`
              : ""),
        }))
        // filter out any empty entries (require a name)
        .filter((m) => m && m.name);

      if (normalized.length === 0) return;

      // Append template medicines to existing medicinesData (preserve existing)
      setMedicinesData((prev = []) => {
        const existing = Array.isArray(prev) ? prev : [];

        const toAdd = [];

        const normalizeStr = (s) =>
          (s ?? "").toString().trim().toLowerCase();

        const isDuplicateAgainst = (candidate, target) => {
          // if either has no name, fallback to field-equality check
          const candName = normalizeStr(candidate.name);
          const targName = normalizeStr(target.name);

          // 1) Exact name match (case-insensitive)
          if (candName && targName && candName === targName)
            return true;

          // 2) Partial name overlap (one contains the other)
          if (
            candName &&
            targName &&
            (candName.includes(targName) ||
              targName.includes(candName))
          )
            return true;

          // 3) All non-empty candidate fields match target fields (name/type/dosage/frequency/duration/howToTake/fromTo)
          const fields = [
            "name",
            "type",
            "dosage",
            "frequency",
            "duration",
            "howToTake",
            "fromTo",
          ];
          const allMatch = fields.every((f) => {
            const a = normalizeStr(target[f]);
            const b = normalizeStr(candidate[f]);
            // if candidate has no value for the field, ignore it in this comparison
            if (!b) return true;
            return a === b;
          });
          if (allMatch) return true;

          return false;
        };

        normalized.forEach((tc) => {
          const existsInExisting = existing.some((ec) =>
            isDuplicateAgainst(tc, ec),
          );
          const existsInToAdd = toAdd.some((added) =>
            isDuplicateAgainst(tc, added),
          );

          if (!existsInExisting && !existsInToAdd) {
            const entry = {
              ...tc,
              no: existing.length + toAdd.length + 1,
            };

            // persist medicine suggestion for autocomplete
            try {
              if (entry.name)
                saveToStorage(
                  STORAGE_KEYS.MEDICINES,
                  entry.name,
                );
            } catch (e) {
              // ignore storage errors
            }

            toAdd.push(entry);
          }
        });

        if (toAdd.length === 0) return existing;
        return [...existing, ...toAdd];
      });
    } catch (err) {
      console.warn("Failed to parse or apply templateData:", err);
    }
  }, [
    templateData,
    // setMedicinesData
  ]);

  return (
    <Box>
      {/* Allergy Conflict Summary */}
      {existingConflicts.length > 0 && (
        <Box
          mb={4}
          p={3}
          bg="red.50"
          // borderRadius="md"
          border="1px solid"
          borderColor="red.200"
        >
          <Alert status="warning" bg="transparent" p={0}>
            <AlertIcon color="red.500" />
            <Box>
              <AlertTitle color="red.700" fontSize="sm">
                ⚠️ Allergy Conflicts Detected in Current
                Prescription
              </AlertTitle>
              <AlertDescription color="red.600" fontSize="sm">
                {existingConflicts.length} medicine(s) may
                conflict with patient allergies:{" "}
                {existingConflicts.map((med, idx) => (
                  <Text as="span" key={idx} fontWeight="bold">
                    {med.name}
                    {idx < existingConflicts.length - 1
                      ? ", "
                      : ""}
                  </Text>
                ))}
              </AlertDescription>
            </Box>
          </Alert>
        </Box>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td
              style={{
                border: "1px solid #E2E8F0",
                padding: "10px",
                background: "white",
                textAlign: "left",
              }}
            >
              <Text
                fontWeight="bold"
                style={{ fontSize: "16px" }}
              >
                Medicines:
              </Text>
              {/* Patient Allergies Info */}
              {allergicMedicines &&
                allergicMedicines.length > 0 && (
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    mt={1}
                    fontStyle="italic"
                  >
                    Patient allergies:{" "}
                    {allergicMedicines.join(", ")}
                  </Text>
                )}
            </td>
            <td
              style={{
                border: "1px solid #E2E8F0",
                padding: "10px",
                background: "white",
                textAlign: "left",
              }}
            >
              <table
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                }}
              >
                <thead>
                  <tr>
                    {/* <th style={{ border: "1px solid #E2E8F0", padding: "5px", fontWeight: "bold" }}>No.</th> */}
                    <th
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      Medicine
                    </th>
                    <th
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      Type
                    </th>
                    <th
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      Dosage
                    </th>
                    <th
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      Frequency
                    </th>
                    <th
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      Duration
                    </th>
                    <th
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      How to take
                    </th>
                    <th
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      From To
                    </th>
                    <th
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Existing Medicines with formatted Frequency Text */}
                  {medicinesData.map((medicine, index) => {
                    const hasAllergyConflict =
                      checkAllergyConflict(medicine.name);
                    return (
                      <tr
                        key={index}
                        style={{
                          backgroundColor:
                            hasAllergyConflict
                              ? "#FED7D7"
                              : "white",
                        }}
                      >
                        {/* <td style={{ border: "1px solid #E2E8F0", padding: "5px" }}>{medicine.no}</td> */}
                        <td
                          style={{
                            border: "1px solid #E2E8F0",
                            padding: "5px",
                            position: "relative",
                          }}
                        >
                          {medicine.name}
                          {/* Allergy Warning for Existing Medicine */}
                          {hasAllergyConflict && (
                            <Box
                              position="absolute"
                              top="-5px"
                              right="-5px"
                              bg="red.500"
                              color="white"
                              borderRadius="full"
                              width="16px"
                              height="16px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="10px"
                              fontWeight="bold"
                              zIndex="5"
                              title={`⚠️ Allergy Alert: Patient is allergic to ${hasAllergyConflict}`}
                            >
                              !
                            </Box>
                          )}
                        </td>
                        <td
                          style={{
                            border: "1px solid #E2E8F0",
                            padding: "5px",
                          }}
                        >
                          {medicine.type}
                        </td>
                        <td
                          style={{
                            border: "1px solid #E2E8F0",
                            padding: "5px",
                          }}
                        >
                          {medicine.dosage}
                        </td>
                        <td
                          style={{
                            border: "1px solid #E2E8F0",
                            padding: "5px",
                          }}
                        >
                          {/* Display readable frequency text for existing medicines */}
                          {getFrequencyText(
                            medicine.frequency,
                          ) || medicine.frequency}
                        </td>
                        <td
                          style={{
                            border: "1px solid #E2E8F0",
                            padding: "5px",
                          }}
                        >
                          {medicine.duration}
                        </td>
                        <td
                          style={{
                            border: "1px solid #E2E8F0",
                            padding: "5px",
                          }}
                        >
                          {medicine.howToTake}
                        </td>
                        <td
                          style={{
                            border: "1px solid #E2E8F0",
                            padding: "5px",
                          }}
                        >
                          {medicine.fromTo}
                        </td>
                        <td
                          style={{
                            border: "1px solid #E2E8F0",
                            padding: "5px",
                          }}
                        >
                          <button
                            style={{
                              backgroundColor: "white",
                                // hasAllergyConflict
                                //   ? "#ff7d7d"
                                //   : "white",
                              color: "red",
                              fontWeight: "bold",
                              fontSize: "16px",
                              border: "none",
                              borderRadius: "50%",
                              cursor: "pointer",
                              padding: "5px 10px",
                              // fontSize: "12px",
                            }}
                            onClick={() =>
                              onDeleteMedicine(
                                index,
                              )
                            }
                            title={
                              hasAllergyConflict
                                ? `⚠️ Remove medicine with allergy conflict (${hasAllergyConflict})`
                                : "Remove medicine"
                            }
                          >
                            {hasAllergyConflict
                              ? "⚠️"
                              : "X"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {/* New Medicine Input Row */}
                  <tr>
                    {/* Medicine Name with Smart Autocomplete and Allergy Highlighting */}
                    <td
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                        }}
                      >
                        <SmartAutocompleteInput
                          value={
                            newMedicineData.name ||
                            ""
                          }
                          onChange={(e) => {
                            const inputValue =
                              e.target.value;
                            setNewMedicineData(
                              (prev) => ({
                                ...prev,
                                name: inputValue,
                              }),
                            );
                          }}
                          onSelect={(
                            value,
                            metadata,
                          ) => {
                            setNewMedicineData(
                              (prev) => ({
                                ...prev,
                                name: value,
                              }),
                            );
                            if (
                              metadata?.isCustomInput ||
                              metadata?.isFromSuggestions
                            ) {
                              saveToStorage(
                                STORAGE_KEYS.MEDICINES,
                                value,
                              );
                            }
                          }}
                          placeholder="Enter Medicine name..."
                          category="medicines"
                          allowCustomInput={true}
                          size="sm"
                          style={{
                            width: "100%",
                            padding: "5px",
                            margin: "0 5px 0 0",
                            borderRadius: "5px",
                            border: checkAllergyConflict(
                              newMedicineData.name,
                            )
                              ? "2px solid #E53E3E"
                              : "1px solid #ccc",
                            backgroundColor:
                              checkAllergyConflict(
                                newMedicineData.name,
                              )
                                ? "#FED7D7"
                                : "white",
                          }}
                          data-testid="medicine-name-input"
                        />
                        {/* Allergy Warning Indicator */}
                        {checkAllergyConflict(
                          newMedicineData.name,
                        ) && (
                            <Box
                              position="absolute"
                              top="-8px"
                              right="-8px"
                              bg="red.500"
                              color="white"
                              borderRadius="full"
                              width="20px"
                              height="20px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="12px"
                              fontWeight="bold"
                              zIndex="10"
                              title={`⚠️ Allergy Alert: Patient is allergic to ${checkAllergyConflict(newMedicineData.name)}`}
                            ></Box>
                          )}
                      </div>
                    </td>

                    {/* Medicine Type */}
                    <td
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                      }}
                    >
                      <select
                        value={newMedicineData.type}
                        onChange={(e) =>
                          setNewMedicineData({
                            ...newMedicineData,
                            type: e.target.value,
                          })
                        }
                        style={{
                          width: "80%",
                          padding: "5px",
                          margin: "0 5px 0 0",
                          borderRadius: "5px",
                          border: "1px solid #ccc",
                        }}
                      >
                        <option value="">
                          Select Type
                        </option>
                        {MEDICINE_TYPES.filter(
                          (t) => t && t.value,
                        )
                          .filter(
                            (t, idx, arr) =>
                              arr.findIndex(
                                (p) =>
                                  p &&
                                  p.value ===
                                  t.value,
                              ) === idx,
                          )
                          .map((type) => (
                            <option
                              key={type.value}
                              value={type.value}
                            >
                              {type.label}
                            </option>
                          ))}
                      </select>
                    </td>

                    {/* Dosage */}
                    <td
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                      }}
                    >
                      <select
                        value={newMedicineData.dosage}
                        onChange={(e) =>
                          setNewMedicineData({
                            ...newMedicineData,
                            dosage: e.target.value,
                          })
                        }
                        style={{
                          width: "80%",
                          padding: "5px",
                          margin: "0 5px 0 0",
                          borderRadius: "5px",
                          border: "1px solid #ccc",
                        }}
                      >
                        <option value="">
                          Select Dosage
                        </option>
                        {DOSAGE_OPTIONS.filter(
                          (d) => d && d.value,
                        )
                          .filter(
                            (d, idx, arr) =>
                              arr.findIndex(
                                (p) =>
                                  p &&
                                  p.value ===
                                  d.value,
                              ) === idx,
                          )
                          .map((dosage) => (
                            <option
                              key={dosage.value}
                              value={dosage.value}
                            >
                              {dosage.label}
                            </option>
                          ))}
                      </select>
                    </td>

                    {/* Frequency */}
                    <td
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                      }}
                    >
                      <select
                        value={
                          newMedicineData.frequency
                        }
                        onFocus={() =>
                          handleFrequencyFocus(
                            newMedicineData.type,
                          )
                        }
                        onChange={(e) => {
                          const selected =
                            e.target.value;
                          // update local state
                          setNewMedicineData({
                            ...newMedicineData,
                            frequency: selected,
                          });

                          // persist frequency usage to both central key and legacy key
                          try {
                            // central storage key (may be defined in CENTRAL_STORAGE_KEYS)
                            const existingCentral =
                              getStoredSuggestions(
                                STORAGE_KEYS.FREQUENCIES,
                              );
                            if (
                              selected &&
                              selected !==
                              "0000" &&
                              !existingCentral.includes(
                                selected,
                              )
                            ) {
                              localStorage.setItem(
                                STORAGE_KEYS.FREQUENCIES,
                                JSON.stringify([
                                  ...existingCentral,
                                  selected,
                                ]),
                              );
                            }

                            // legacy key for compatibility: 'medicinesFreq'
                            const rawLegacy =
                              localStorage.getItem(
                                "medicinesFreq",
                              ) || "[]";
                            const legacyArr =
                              JSON.parse(
                                rawLegacy,
                              );
                            if (
                              selected &&
                              selected !==
                              "0000" &&
                              Array.isArray(
                                legacyArr,
                              ) &&
                              !legacyArr.includes(
                                selected,
                              )
                            ) {
                              localStorage.setItem(
                                "medicinesFreq",
                                JSON.stringify([
                                  ...legacyArr,
                                  selected,
                                ]),
                              );
                            }

                            // Update ordered cache immediately so UI reflects the change
                            const base =
                              newMedicineData.type ===
                                "Eye Drop"
                                ? EYE_DROP_FREQUENCIES.filter(
                                  (f) =>
                                    f &&
                                    f.value,
                                )
                                : newMedicineData.type ===
                                  "Eye Ointment"
                                  ? EYE_OINTMENT_FREQUENCIES.filter(
                                    (f) =>
                                      f &&
                                      f.value,
                                  )
                                  : newMedicineData.type ===
                                    "Skin Ointment"
                                    ? SKIN_OINTMENT_FREQUENCIES.filter(
                                      (f) =>
                                        f &&
                                        f.value,
                                    )
                                    : FREQUENCY_PATTERNS.filter(
                                      (
                                        pattern,
                                      ) =>
                                        (
                                          pattern.value ||
                                          ""
                                        ).toString() !==
                                        "0000",
                                    ).filter(
                                      (
                                        pattern,
                                      ) =>
                                        pattern &&
                                        pattern.value,
                                    );
                            const updatedOrdered =
                              computeOrderedFrequencies(
                                base,
                              );
                            setOrderedFrequencyOptions(
                              updatedOrdered,
                            );
                          } catch (err) {
                            console.error(
                              "Failed to persist selected frequency:",
                              err,
                            );
                          }
                        }}
                        style={{
                          width: "80%",
                          padding: "5px",
                          margin: "0 5px 0 0",
                          borderRadius: "5px",
                          border: "1px solid #ccc",
                        }}
                      >
                        <option value="">
                          Select Frequency
                        </option>
                        {(() => {
                          // determine base option list based on type
                          const base =
                            newMedicineData.type ===
                              "Eye Drop"
                              ? EYE_DROP_FREQUENCIES.filter(
                                (f) =>
                                  f &&
                                  f.value,
                              )
                              : newMedicineData.type ===
                                "Eye Ointment"
                                ? EYE_OINTMENT_FREQUENCIES.filter(
                                  (f) =>
                                    f &&
                                    f.value,
                                )
                                : newMedicineData.type ===
                                  "Skin Ointment"
                                  ? SKIN_OINTMENT_FREQUENCIES.filter(
                                    (f) =>
                                      f &&
                                      f.value,
                                  )
                                  : FREQUENCY_PATTERNS.filter(
                                    (
                                      pattern,
                                    ) =>
                                      (
                                        pattern.value ||
                                        ""
                                      ).toString() !==
                                      "0000",
                                  ).filter(
                                    (
                                      pattern,
                                    ) =>
                                      pattern &&
                                      pattern.value,
                                  );

                          const listToRender =
                            orderedFrequencyOptions &&
                              orderedFrequencyOptions.length
                              ? orderedFrequencyOptions
                              : base;

                          // ensure uniqueness and stable ordering while rendering
                          const seen = new Set();
                          return listToRender
                            .filter(
                              (f) => f && f.value,
                            )
                            .filter((f) => {
                              if (
                                seen.has(
                                  f.value,
                                )
                              )
                                return false;
                              seen.add(f.value);
                              return true;
                            })
                            .map((freq) => (
                              <option
                                key={freq.value}
                                value={
                                  freq.value
                                }
                                title={
                                  freq.description ||
                                  freq.label
                                }
                              >
                                {freq.label}
                              </option>
                            ));
                        })()}
                      </select>
                    </td>

                    {/* Duration */}
                    <td
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                      }}
                    >
                      <select
                        value={newMedicineData.duration}
                        onChange={onDurationChange}
                        style={{
                          width: "80%",
                          padding: "5px",
                          margin: "0 5px 0 0",
                          borderRadius: "5px",
                          border: "1px solid #ccc",
                        }}
                      >
                        <option value="">
                          Select Duration
                        </option>
                        {DURATION_OPTIONS.filter(
                          (d) => d && d.value,
                        )
                          .filter(
                            (d, idx, arr) =>
                              arr.findIndex(
                                (p) =>
                                  p &&
                                  p.value ===
                                  d.value,
                              ) === idx,
                          )
                          .map((duration) => (
                            <option
                              key={duration.value}
                              value={
                                duration.value
                              }
                            >
                              {duration.label}
                            </option>
                          ))}
                      </select>
                    </td>

                    {/* How to take */}
                    <td
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                      }}
                    >
                      <select
                        value={
                          newMedicineData.howToTake
                        }
                        onChange={(e) =>
                          setNewMedicineData({
                            ...newMedicineData,
                            howToTake:
                              e.target.value,
                          })
                        }
                        style={{
                          width: "80%",
                          padding: "5px",
                          margin: "0 5px 0 0",
                          borderRadius: "5px",
                          border: "1px solid #ccc",
                        }}
                      >
                        <option value="">
                          Select How to Take
                        </option>
                        {HOW_TO_TAKE_OPTIONS.filter(
                          (o) => o && o.value,
                        )
                          .filter(
                            (o, idx, arr) =>
                              arr.findIndex(
                                (p) =>
                                  p &&
                                  p.value ===
                                  o.value,
                              ) === idx,
                          )
                          .map((option) => (
                            <option
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </option>
                          ))}
                      </select>
                    </td>

                    {/* From/To Dates - Auto-calculated based on duration */}
                    <td
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <Text
                            style={{
                              fontSize: "13px",
                              minWidth: "35px",
                            }}
                          >
                            From:
                          </Text>
                          <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => {
                              setFromDate(
                                e.target.value,
                              );
                              // Auto-calculate "To" date if duration is already selected
                              if (
                                e.target
                                  .value &&
                                newMedicineData.duration
                              ) {
                                // Calculate "To" date based on "From" date and duration
                                const calculateToDate =
                                  (
                                    fromDate,
                                    duration,
                                  ) => {
                                    if (
                                      !fromDate ||
                                      !duration
                                    )
                                      return "";
                                    const date =
                                      new Date(
                                        fromDate,
                                      );
                                    const [
                                      value,
                                      unit,
                                    ] =
                                      duration.split(
                                        " ",
                                      );

                                    switch (
                                    unit
                                    ) {
                                      case "day":
                                      case "days":
                                        date.setDate(
                                          date.getDate() +
                                          parseInt(
                                            value,
                                          ),
                                        );
                                        break;
                                      case "week":
                                      case "weeks":
                                        date.setDate(
                                          date.getDate() +
                                          parseInt(
                                            value,
                                          ) *
                                          7,
                                        );
                                        break;
                                      case "month":
                                      case "months":
                                        const months =
                                          parseFloat(
                                            value,
                                          );
                                        const wholeMonths =
                                          Math.floor(
                                            months,
                                          );
                                        const decimalMonths =
                                          months -
                                          wholeMonths;
                                        date.setMonth(
                                          date.getMonth() +
                                          wholeMonths,
                                        );
                                        if (
                                          decimalMonths >
                                          0
                                        ) {
                                          const daysInMonth =
                                            new Date(
                                              date.getFullYear(),
                                              date.getMonth() +
                                              1,
                                              0,
                                            ).getDate();
                                          const additionalDays =
                                            Math.round(
                                              daysInMonth *
                                              decimalMonths,
                                            );
                                          date.setDate(
                                            date.getDate() +
                                            additionalDays,
                                          );
                                        }
                                        break;
                                      default:
                                        return "";
                                    }
                                    return date
                                      .toISOString()
                                      .split(
                                        "T",
                                      )[0];
                                  };

                                const calculatedToDate =
                                  calculateToDate(
                                    e.target
                                      .value,
                                    newMedicineData.duration,
                                  );
                                setToDate(
                                  calculatedToDate,
                                );
                              }
                            }}
                            style={{
                              padding: "3px",
                              borderRadius: "3px",
                              border: "1px solid #ccc",
                              fontSize: "12px",
                              width: "120px",
                            }}
                            title="Start date - will auto-calculate end date when duration is selected"
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <Text
                            style={{
                              fontSize: "13px",
                              minWidth: "35px",
                            }}
                          >
                            To:
                          </Text>
                          <input
                            type="date"
                            value={toDate}
                            onChange={(e) =>
                              setToDate(
                                e.target.value,
                              )
                            }
                            style={{
                              padding: "3px",
                              borderRadius: "3px",
                              border: "1px solid #ccc",
                              fontSize: "12px",
                              width: "120px",
                              backgroundColor:
                                toDate
                                  ? "#f0f8ff"
                                  : "white",
                            }}
                            title="End date (auto-calculated from duration)"
                          />
                        </div>
                        {/* Show calculation info when dates are auto-calculated */}
                        {fromDate &&
                          toDate &&
                          newMedicineData.duration && (
                            <Text
                              style={{
                                fontSize:
                                  "10px",
                                color: "#666",
                                fontStyle:
                                  "italic",
                                textAlign:
                                  "center",
                              }}
                            >
                              Auto-calculated from{" "}
                              {
                                newMedicineData.duration
                              }
                            </Text>
                          )}
                      </div>
                    </td>

                    {/* Add Button with Allergy Check */}
                    <td
                      style={{
                        border: "1px solid #E2E8F0",
                        padding: "5px",
                      }}
                    >
                      <button
                        style={{
                          backgroundColor:
                            checkAllergyConflict(
                              newMedicineData.name,
                            )
                              ? "#E53E3E"
                              : "#4CAF50",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          cursor: "pointer",
                          padding: "5px 10px",
                          fontSize: "12px",
                        }}
                        onClick={() => {
                          // persist selected frequency to storage for doctor recommendations (avoid storing '0000')
                          try {
                            const freq =
                              newMedicineData.frequency;
                            if (
                              freq &&
                              freq !== "0000"
                            ) {
                              const existing =
                                getStoredSuggestions(
                                  STORAGE_KEYS.FREQUENCIES,
                                );
                              if (
                                !existing.includes(
                                  freq,
                                )
                              ) {
                                localStorage.setItem(
                                  STORAGE_KEYS.FREQUENCIES,
                                  JSON.stringify(
                                    [
                                      ...existing,
                                      freq,
                                    ],
                                  ),
                                );
                              }
                            }
                          } catch (e) {
                            console.error(
                              "Failed to save frequency to storage",
                              e,
                            );
                          }

                          handleAddMedicineWithAllergyCheck();
                        }}
                        title={
                          checkAllergyConflict(
                            newMedicineData.name,
                          )
                            ? `⚠️ Warning: Patient is allergic to ${checkAllergyConflict(newMedicineData.name)}`
                            : "Add medicine to prescription"
                        }
                      >
                        {checkAllergyConflict(
                          newMedicineData.name,
                        )
                          ? "⚠️ Add"
                          : "Add"}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Allergy Conflict Confirmation Modal */}
      <Modal
        isOpen={isAllergyModalOpen}
        onClose={cancelAddMedicine}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text color="red.500" fontWeight="bold">
              ⚠️ Allergy Alert
            </Text>
          </ModalHeader>
          <ModalBody>
            <Alert status="warning" mb={4}>
              <AlertIcon />
              <Box>
                <AlertTitle>
                  Medicine Allergy Detected!
                </AlertTitle>
                <AlertDescription>
                  The medicine you are trying to add may
                  conflict with the patient's known allergies.
                </AlertDescription>
              </Box>
            </Alert>

            <Box
              p={4}
              bg="red.50"
              borderRadius="md"
              border="1px solid"
              borderColor="red.200"
            >
              <Text fontWeight="bold" mb={2}>
                Conflict Details:
              </Text>
              <Text mb={2}>
                <strong>Medicine:</strong>{" "}
                {pendingMedicineData?.name}
              </Text>
              <Text mb={2}>
                <strong>Known Allergy:</strong>{" "}
                {conflictingMedicine}
              </Text>
              <Text
                fontSize="sm"
                color="red.600"
                fontStyle="italic"
              >
                {getConflictMessage(
                  pendingMedicineData?.name,
                  conflictingMedicine,
                )}
              </Text>
            </Box>

            <Box
              mt={4}
              p={3}
              bg="yellow.50"
              borderRadius="md"
              border="1px solid"
              borderColor="yellow.200"
            >
              <Text
                fontSize="sm"
                fontWeight="medium"
                color="yellow.800"
              >
                Are you sure you want to add this medicine to
                the prescription?
              </Text>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="red"
              mr={3}
              onClick={confirmAddMedicineWithAllergy}
              size="sm"
            >
              Yes, Add Anyway
            </Button>
            <Button
              variant="outline"
              onClick={cancelAddMedicine}
              size="sm"
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MedicinesSection;
